type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

const memoryCache = new Map<string, CacheEntry<unknown>>();

const getStorageKey = (key: string) => `lawx_cache:${key}`;

const readStorage = <T>(key: string): CacheEntry<T> | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(getStorageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed?.expiresAt) return null;
    if (parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(getStorageKey(key));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeStorage = <T>(key: string, entry: CacheEntry<T>) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(getStorageKey(key), JSON.stringify(entry));
  } catch {
    // ignore storage errors
  }
};

export const getCachedValue = <T>(key: string): T | null => {
  const memory = memoryCache.get(key) as CacheEntry<T> | undefined;
  if (memory && memory.expiresAt > Date.now()) {
    return memory.value;
  }
  const stored = readStorage<T>(key);
  if (stored) {
    memoryCache.set(key, stored as CacheEntry<unknown>);
    return stored.value;
  }
  return null;
};

export const setCachedValue = <T>(key: string, value: T, ttlMs: number) => {
  const entry: CacheEntry<T> = {
    expiresAt: Date.now() + ttlMs,
    value,
  };
  memoryCache.set(key, entry as CacheEntry<unknown>);
  writeStorage(key, entry);
};
